/**
 * Implamentation of Modal component using Tailwind
 *
 * @author - Harsh Tomar
 */

// Dependencies
import React, { Fragment, MouseEvent, ReactElement, useEffect, useRef } from "react"
import { Dialog, Transition } from '@headlessui/react'

type Props = {
  visible: boolean
  children: ReactElement
  direction?: "left" | "centre" | "right"
  noPadding?: boolean
  onClose?: () => void
}

export default function Modal(props: Props) {
  const overlayRef = useRef<HTMLDivElement>(null)
  const dialogRef = useRef<HTMLDivElement>(null)

  function _handleClose() {
    // if (e.target !== e.currentTarget) return false

    props.onClose && props.onClose()
  }

  useEffect(() => {
    if (props.visible) {
      document.body.style.overflow = "hidden"
    } else {
      document.body.style.overflow = "auto"
    }
  }, [props.visible])

  function resolveDialogClassNames() {
    switch (props.direction) {
      case "right":
        return `bg-white h-screen w-10/12 sm:w-6/12 md:w-4/12 lg:w-3/12 xl:w-2/12 transition-all duration-300`
      case "centre":
        return `bg-white rounded-xl mx-auto mb-4 md:mb-0 top-2/4 ${
          props.noPadding ? "" : "p-4"
        } w-11/12 sm:w-5/6 md:w-6/12 lg:w-4/12 xl:w-3/12 transition duration-300`

      default:
        return `bg-white rounded-xl mx-auto mb-4 md:mb-0 top-2/4 ${
          props.noPadding ? "" : "p-4"
        } w-11/12 sm:w-5/6 md:w-6/12 lg:w-4/12 xl:w-3/12 transition duration-300`
    }
  }

  return (
    <Transition appear show={props.visible} as={Fragment}>
        <Dialog as="div" className="relative z-10" onClose={_handleClose}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-50" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className={`w-full max-w-md transform overflow-hidden rounded-2xl bg-white ${props.noPadding ? 'p-0' : 'p-6'} text-left align-top shadow-xl transition-all`}>
                  {props.children}
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
  )
}

// Modal.defaultProps = {
//   direction: ce
// }
